import { useState, useCallback } from 'react';
import { Notification } from '../types';
import { getNotifications, addNotification, updateNotification } from '../services/storageService';
import { requireCompanyId } from './requireCompanyId';

export function useNotifications(companyId: string | null) {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runAction = useCallback(async <T>(action: (cid: string) => Promise<T>): Promise<T> => {
    setLoading(true);
    setError(null);
    try {
      const cid = requireCompanyId(companyId);
      return await action(cid);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unexpected error';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  const load = useCallback(async () => {
    const data = await runAction((cid) => getNotifications(cid));
    setNotifications(data);
    return data;
  }, [runAction]);

  const add = useCallback(async (notif: Notification) => {
    const updated = await runAction((cid) => addNotification(notif, cid));
    setNotifications(updated);
    return updated;
  }, [runAction]);

  const update = useCallback(async (notif: Notification) => {
    const updated = await runAction((cid) => updateNotification(notif, cid));
    setNotifications(updated);
    return updated;
  }, [runAction]);

  return { notifications, setNotifications, loading, error, load, add, update };
}
